import React from 'react';
import { clsx } from 'clsx';
import { 
  HiSquares2X2,
  HiTableCells,
  HiInboxStack
} from 'react-icons/hi2';
import { Button } from '@/components/Button';
import type { ViewToggleProps, TicketViewType } from '@/types/ui';

/**
 * View Toggle Component - Switch between Card, Table, and Inbox views
 * Provides intuitive icons and labels for different ticket list layouts
 */
export const ViewToggle: React.FC<ViewToggleProps> = ({
  currentView,
  onViewChange,
  disabled = false,
  showLabels = false,
  size = 'md',
  className,
  'data-testid': testId,
  ...props
}) => {
  // View configuration with icons, labels, and descriptions (ordered by preference)
  const viewConfigs: Record<TicketViewType, {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    description: string;
    order: number;
  }> = {
    table: {
      icon: HiTableCells,
      label: 'Table View',
      description: 'Display tickets in a detailed table format',
      order: 1
    },
    card: {
      icon: HiSquares2X2,
      label: 'Card View',
      description: 'Display tickets as cards in a grid layout',
      order: 2
    },
    inbox: {
      icon: HiInboxStack,
      label: 'Inbox View',
      description: 'Display tickets in an email-like inbox format',
      order: 3
    }
  };

  // Size configurations
  const sizeConfig = {
    sm: {
      button: 'px-2 py-1',
      icon: 'w-4 h-4',
      text: 'text-xs',
      gap: 'space-x-1'
    },
    md: {
      button: 'px-3 py-2',
      icon: 'w-5 h-5',
      text: 'text-sm',
      gap: 'space-x-2'
    },
    lg: {
      button: 'px-4 py-3',
      icon: 'w-6 h-6',
      text: 'text-base',
      gap: 'space-x-3'
    }
  };

  const config = sizeConfig[size];

  const handleViewChange = (view: TicketViewType) => {
    if (!disabled && view !== currentView) {
      onViewChange(view);
    }
  };

  const toggleClasses = clsx(
    // Base styles
    'inline-flex',
    'items-center',
    'rounded-lg',
    'border',
    'border-gray-200',
    'bg-white',
    'shadow-sm',
    'transition-all',
    'duration-200',
    
    // Disabled state
    disabled && 'opacity-50 cursor-not-allowed',
    
    className
  );

  const getButtonClasses = (view: TicketViewType, isActive: boolean) => {
    return clsx(
      // Base button styles
      'inline-flex',
      'items-center',
      'transition-all',
      'duration-200',
      'rounded-md',
      'border',
      'font-medium',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-primary-500',
      'focus:ring-offset-2',
      
      // Size-specific classes
      config.button,
      config.gap,
      config.text,
      
      // Active/inactive states
      isActive ? [
        'bg-primary-50',
        'border-primary-200',
        'text-primary-700',
        'shadow-sm'
      ] : [
        'bg-white',
        'border-transparent',
        'text-gray-600',
        'hover:bg-gray-50',
        'hover:border-gray-200',
        'hover:text-gray-900'
      ],
      
      // Disabled state
      disabled && 'cursor-not-allowed hover:bg-white hover:border-transparent hover:text-gray-600'
    );
  };

  return (
    <div
      className={toggleClasses}
      data-testid={testId}
      role="tablist"
      aria-label="View options"
      {...props}
    >
      {(Object.entries(viewConfigs) as [TicketViewType, typeof viewConfigs[TicketViewType]][])
        .sort(([, a], [, b]) => a.order - b.order)
        .map(([view, viewConfig], index) => {
          const Icon = viewConfig.icon;
          const isActive = currentView === view;
          
          return (
            <button
              key={view}
              className={getButtonClasses(view, isActive)}
              onClick={() => handleViewChange(view)}
              disabled={disabled}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${view}-view-panel`}
              title={viewConfig.description}
              type="button"
            >
              <Icon className={clsx(config.icon, 'flex-shrink-0')} />
              {showLabels && (
                <span className="font-medium">
                  {viewConfig.label}
                </span>
              )}
            </button>
          );
        }
      )}
    </div>
  );
};

/**
 * Compact View Toggle - Simplified version with just icons
 */
export const CompactViewToggle: React.FC<Omit<ViewToggleProps, 'showLabels' | 'size'>> = (props) => {
  return (
    <ViewToggle
      {...props}
      showLabels={false}
      size="sm"
    />
  );
};

/**
 * View Toggle with Labels - Full version with labels
 */
export const LabeledViewToggle: React.FC<Omit<ViewToggleProps, 'showLabels'>> = (props) => {
  return (
    <ViewToggle
      {...props}
      showLabels={true}
    />
  );
};

// Attach variants to main ViewToggle component
(ViewToggle as any).Compact = CompactViewToggle;
(ViewToggle as any).Labeled = LabeledViewToggle;